import { Injectable } from '@angular/core';
import { Error as FoundationError, isError } from 'fl-foundation';
import { AuthorizationData } from 'fl-platform-authorizer';
import { BehaviorSubject } from 'rxjs';
import { DataService } from 'src/app/data.service';
import { LANG } from '../common/const/common.const';
import { KEYS } from '../utils/constants/storage.constants';
import { AuthService } from './auth/auth.service';
import { ConfigService } from './config.service';
import { RefreshService } from './evergent/login/refresh.service';
import { LocalStorageService } from './storage/local-storage.service';
import { SessionStorageService } from './storage/session-storage.service';
import { GeoLocationService } from './geolocation.service';
import { SignOutService } from '../shared-components/exit-app-and-sign-out/sign-out/sign-out.service';

@Injectable()
export class AppLoadService {
  private elapsedSplash: number;

  constructor(
    private readonly configService: ConfigService,
    private readonly refreshService: RefreshService,
    private readonly dataService: DataService,
    private readonly authService: AuthService,
    private readonly localStorage: LocalStorageService,
    private readonly sessionStorage: SessionStorageService,
    private readonly geoLocation: GeoLocationService,
    private readonly signOutService: SignOutService
  ) {}

  public async initializeApp(): Promise<void> {
    this.sessionStorage.clear();
    this.elapsedSplash = new Date().getTime();
    this.sessionStorage.setItem(KEYS.ELAPSED_SPLASH, '' + this.elapsedSplash);

    // 1. Capture the initial URL for deeplink handling before router starts
    const currentUrl = window.location.pathname;
    const isStandardDeeplink =
      currentUrl &&
      currentUrl !== '/' &&
      currentUrl !== '/onboarding' &&
      currentUrl !== '/new-preferred-language-selection';

    if (isStandardDeeplink) {
      console.log(
        '💾 AppLoadService: Saving initial pending deeplink URL:',
        currentUrl
      );
      this.dataService.setPendingDeeplinkUrl = currentUrl;
    }

    try {
      const preferredLanguages = this.localStorage.getItem(
        KEYS.ANONYMOUS_PREFERRED_LANGUAGES
      );

      // Set skip flag: true if preferred languages exist, false if not logged in
      // For logged-in users without preferred languages, CoreResolver will handle the check
      const hasPreferredLanguages =
        preferredLanguages && preferredLanguages.length > 0;
      if (hasPreferredLanguages) {
        this.dataService.skipPreferredLanguageSelection = true;
      } else {
        this.dataService.skipPreferredLanguageSelection =
          this.authService.isLoggedIn();
      }

      // Trigger token expiry migration for legacy users
      if (this.authService.isLoggedIn()) {
        this.authService.getAccessTokenExpiry();
      }

      const displayLang = this.localStorage.getItem(KEYS.DISPLAY_LANGUAGE);
      if (!displayLang || ![LANG.TG.k, LANG.TM.k].includes(displayLang)) {
        this.localStorage.setItem(KEYS.DISPLAY_LANGUAGE, LANG.EN.k);
      }

      // Calculate time to download config
      const confStart = new Date().getTime();
      this.localStorage.setItem('confStart', '' + confStart);

      // Load config first as it's needed for tokens
      await this.configService.getConfig();
      const configTime = new Date().getTime() - confStart;
      this.dataService.configTime = configTime;

      // Get tokens (platform token must complete before evergent token)
      await this.getTokens();
      await this.geoLocation.getGeolocation();
    } catch (e) {
      // Always show loader even if initialization fails
      console.error('App initialization error:', e);
    }
  }

  private async getTokens(): Promise<void> {
    await this.getEvergentToken();
    await this.getPlatformToken();
  }

  public async getPlatformToken(): Promise<AuthorizationData> {
    try {
      this.authService.resetPlayback();
      const authData = await this.authService.initPlatformService();
      if (isError(authData)) {
        throw authData;
      }
      return authData;
    } catch (e) {
      this.authService.resetPlayback();
      await this.authService.initPlatformService();
      throw e;
    }
  }

  private async getEvergentToken(): Promise<void> {
    const appConfig = this.configService.applicationConfig;
    if (this.authService.isLoggedIn()) {
      try {
        await this.refreshService.makeRefreshTokenCall(appConfig);
      } catch (e) {
        console.warn(
          'AppLoadService: Initial token refresh failed, continuing as guest:',
          e
        );
        this.signOutService.clearData();
      }
    } else {
      this.localStorage.setItem(KEYS.EVERGENT_ACCESS_TOKEN, '');
    }
  }
}
